import {
  LineAddonType,
  Discount,
  OrderLine,
  Payment,
  OrderSession,
  LineType,
  OrderSessionState,
  PaymentMethod,
  PaymentProcessor,
  PaymentState,
  TransactionType,
  MultiCurrencyInfo,
  AdyenPaymentInfo,
  Voucher,
  VATType,
  Currency,
  DiscountConfig,
  OrderType,
  OrderLineItemType,
} from "@smeetz/order-common";
import { LogLevel, LogService } from "@/services/logging";
import {
  AccountingState,
  IAccounting,
  PaymentMethods,
  TransactionTypes,
} from "@/types/db/accounting";
import {
  BaseOrderSession,
  BookingState,
  IProductSlotBooking,
} from "@/types/db/product-slot-booking";
import {
  BookingStatus,
  IProductSlotState,
  IQuantifiedProductSlotState,
  PSSType,
} from "@/types/db/product-slot-state";
import { getpssExtension, groupedPSS } from "../vue-helpers/booking";
import { generateUuidv4 } from "./app";
import { roundToTwoDecimals, sumArray } from "./common";
import { getPSSValue } from "./product-slot-state";
import { getTaxValue } from "./tax";
import dayjs from "dayjs";
import { Currencies } from "@/constants/currency";
import { getOrderSessionById, isOrderSessionSigned } from "@/api/gql/badges";
import BookingModule from "@/store/modules/booking";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on i18n.global.t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { cloneDeep } from "lodash";
import {
  isCroatianCompliant,
  isFrenchCompliant,
  isGermanCompliant,
  isSpanishCompliant,
} from "@/utils/vue-helpers/compliance";

/**
 * Translates a booking to an order session object
 * @param booking
 * @param currentOrderSessionId order session to which current order lines belong
 */
export async function convertBookingToOrderSession(
  booking: IProductSlotBooking,
  orderSessionId: string
) {
  const baseOrderSession = booking.orderSessions.find(
    (os) => os.id === orderSessionId
  );
  if (!baseOrderSession) {
    LogService.info(`No order session with id ${orderSessionId} in booking`);
    return;
  }
  const startTime = performance.now();
  // Cross-session fiscalization (including unfiscalized tickets from other sessions) only applies to French/Spanish compliant groups.
  // For other countries, use current time so all cross-session accountings are skipped.
  const lastSignedOnPOS =
    isFrenchCompliant.value || isSpanishCompliant.value
      ? await lastTimeSignedOnPOS(booking, orderSessionId)
      : dayjs();
  const orderSession: OrderSession = {
    id: orderSessionId,
    orderId: booking.uuid as string,
    currency: baseOrderSession.currency,
    reference: baseOrderSession.reference,
    state: baseOrderSession.state,
    completionDate: baseOrderSession.completionDate
      ? dayjs(baseOrderSession.completionDate).toDate()
      : undefined,
    visitDate: baseOrderSession.visiteDate
      ? dayjs(baseOrderSession.visiteDate).toDate()
      : undefined,
    orderLines: [], // JSON array of OrderLine interface
    discounts: [], // JSON array of Discount interface
    vouchers: [], // JSON array of Voucher interface
    voucher: 0, // Total vouchers value applied
    discount: 0, // Total discount value
    sales: 0, // Only PSS totals (without discounts and excluded VAT)
    vatExcluded: 0, // Total VAT excluded in this order
    vatIncluded: 0, // Total VAT included in this order
    payments: [], // JSON array of Payment interface
    toPay: [], // Array of "to pay" information
    payment: 0, // Payment total, doesn't include voucher total
    balance: 0, // Calculated balance: discount + voucher + payment + vatExcluded - sales
    fees: 0,
    groupId: baseOrderSession.groupId,
    sellerId: baseOrderSession.sellerId,
    sellerCogn: baseOrderSession.sellerCogn,
    channel: "Smeetz POS", // Sales channel
    sessionStart: dayjs(baseOrderSession.sessionStart).toDate(),
    sessionEnd: baseOrderSession.sessionEnd
      ? dayjs(baseOrderSession.sessionEnd).toDate()
      : undefined,
    expireAt: baseOrderSession.expireAt
      ? dayjs(baseOrderSession.expireAt).toDate()
      : undefined, // TODO: check expiration time
    counterId: baseOrderSession.counterId, // ID of the counter used for POS or kiosk
    seqNumber: baseOrderSession.seqNumber, // TODO: Sequential order number from the counter
    createdAt: dayjs(baseOrderSession.createdAt).toDate(), // Creation datetime
    updatedAt: dayjs(baseOrderSession.updatedAt).toDate(), // Last update datetime
  };
  // set order info
  orderSession.orderLines = await getOrderLinesFromBooking(
    booking,
    orderSessionId,
    lastSignedOnPOS
  );

  if (orderSession.orderLines.length) {
    // order session has orderlines and can be signed
    BookingModule.setOrderSessionHasOrderLines({
      bookingUuid: booking.uuid as string,
      orderSessionId: orderSessionId,
      hasOrderLines: true,
    });
  }
  let discounts: Discount[] = [];
  let discountConfigs: DiscountConfig[] = [];
  let vouchers: Voucher[] = [];
  try {
    const discountsAndVouchers = await getOrderDiscountsAndVouchersFromBooking(
      booking,
      orderSessionId,
      orderSession.orderLines,
      lastSignedOnPOS
    );
    discounts = discountsAndVouchers.discounts;
    discountConfigs = discountsAndVouchers.discountConfigs;
    vouchers = discountsAndVouchers.vouchers;
  } catch (error) {
    LogService.info("Error getting order discounts and vouchers", { error });
  }
  orderSession.vouchers = vouchers;
  orderSession.voucher = sumArray(vouchers.map((v) => v.value));

  orderSession.discounts = discounts;
  orderSession.discount = sumArray(discounts.map((d) => d.value));

  orderSession.discountConfigs = discountConfigs;

  orderSession.payments = await getOrderPaymentsFromBooking(
    booking,
    orderSessionId,
    lastSignedOnPOS
  );
  orderSession.payment = roundToTwoDecimals(
    sumArray(orderSession.payments.map((d) => d.value))
  );

  orderSession.sales = roundToTwoDecimals(
    sumArray(orderSession.orderLines.map((o) => o.totalPrice))
  );

  const { vatIncluded, vatExcluded } = getOrderVatByType(
    orderSession.orderLines
  );
  orderSession.vatIncluded = vatIncluded;
  orderSession.vatExcluded = vatExcluded;

  // Belgian cash rounding is reported as its own (negated) payment line, so it
  // has to be added back for the balance to close.
  const totalRounding = sumArray(
    orderSession.payments
      .filter(
        (payment) => payment.paymentMethod === PaymentMethod.CASH_ROUNDING
      )
      .map((payment) => payment.value)
  );

  orderSession.balance = roundToTwoDecimals(
    orderSession.sales +
      totalRounding +
      orderSession.vatExcluded -
      (orderSession.payment + orderSession.discount + orderSession.voucher)
  );

  const currentOrderPSS = booking.pssList.filter(
    (pss) =>
      pss.orderSessionId === orderSessionId &&
      pss.bookingStatus !== BookingStatus.Abandoned
  );
  orderSession.fees = sumArray(
    currentOrderPSS.map((pss) =>
      pss.bookingStatus === BookingStatus.Canceled
        ? -1 * pss.paymentFeeCollected
        : pss.paymentFeeCollected
    )
  );
  const duration = Math.round(performance.now() - startTime);
  LogService.info(`Booking converted to order session in ${duration}ms`, {
    orderSession,
  });
  return orderSession;
}

/**
 * In France and potentially other countries, if there are tickets that were not booked on the POS (or on uncertified POS) but have payments on it, we need to fiscalize them as well.
 * This allows payments later on to be linked to legitimate order lines.
 */
async function getUnfiscalizedPss(
  booking: IProductSlotBooking,
  orderSessionId: string,
  lastSignedOnPOS: dayjs.Dayjs
): Promise<{
  unfiscalizedPss: Set<string>;
  canceledPssNotSignedWhenPurchased: Set<string>;
}> {
  const getLinkedPssIds = (accounting: IAccounting) =>
    accounting.accountingEntriesList.map((ae) => ae.productSlotStateUuid);

  // Find tickets that were made outside the POS or on POS devices that do not have the certified version,
  // by finding their ticket accountings that should normally have channel != POS
  const unfiscalizedPss = new Set<string>();
  const canceledPssNotSignedWhenPurchased = new Set<string>();

  for (const accounting of booking.accountings) {
    if (
      accounting.transactionType !== TransactionTypes.TRANSACTION_TYPE_TICKET ||
      dayjs(accounting.createdAt).isSameOrBefore(lastSignedOnPOS)
    ) {
      continue;
    }

    const linkedIds = new Set(getLinkedPssIds(accounting));

    for (const pss of booking.pssList) {
      if (
        accounting.orderSessionId !== orderSessionId &&
        pss.bookingStatus !== BookingStatus.Abandoned &&
        linkedIds.has(pss.uuid) &&
        pss.orderSessionId !== orderSessionId
      ) {
        unfiscalizedPss.add(pss.uuid as string); // all pss should normally have uuid
      }

      if (
        accounting.refund === 1 &&
        pss.bookingStatus !== BookingStatus.Abandoned &&
        linkedIds.has(pss.uuid) &&
        pss.orderSessionId !== orderSessionId
      ) {
        // if the cancellation was made outside the POS or after the last time the booking was signed on the POS,
        // then we consider that the canceled tickets were not signed when they were purchased
        // which means that in addition to their cancellation lines, they also need to be included as sale lines for them to cancel out.
        const originalTicketAccounting = booking.accountings.find(
          (acc) =>
            acc.transactionType === TransactionTypes.TRANSACTION_TYPE_TICKET &&
            acc.refund === 0 &&
            acc.accountingEntriesList.some(
              (ae) => ae.productSlotStateUuid === pss.uuid
            )
        );
        if (
          originalTicketAccounting &&
          dayjs(originalTicketAccounting.createdAt).isAfter(lastSignedOnPOS)
        ) {
          LogService.info(
            `Canceled PSS ${pss.uuid} were not signed when purchased, including them in order lines so they can be signed on cancellation.`,
            { accountingId: accounting.uuid }
          );
          canceledPssNotSignedWhenPurchased.add(pss.uuid as string); // all pss should normally have uuid
        }
      }
    }
  }

  return { unfiscalizedPss, canceledPssNotSignedWhenPurchased };
}

/**
 * Translates booking grouped PSS into order lines
 * @param booking
 */
async function getOrderLinesFromBooking(
  booking: IProductSlotBooking,
  orderSessionId: string,
  lastSignedOnPOS: dayjs.Dayjs
): Promise<OrderLine[]> {
  const orderLines: OrderLine[] = [];
  /**
   * since canceled pss may not belong to the current order session,
   * their cancelation lines will be determined from refund ticket accountings that belong to the target order session
   * sale lines on the other hand are simple because pss will be part of the current order
   */
  const canceledPSSIds: (string | null)[] = [];
  for (let i = 0; i < booking.accountings.length; i++) {
    const accounting = booking.accountings[i];
    if (
      accounting.transactionType !== TransactionTypes.TRANSACTION_TYPE_TICKET ||
      accounting.refund !== 1 ||
      accounting.orderSessionId !== orderSessionId
    ) {
      continue;
    }
    const linkedPSS = accounting.accountingEntriesList.map(
      (ae) => ae.productSlotStateUuid
    );
    canceledPSSIds.push(...linkedPSS);
  }

  // Unfiscalized PSS are tickets that were booked outside the POS but are being paid for in the current order session, and thus need to be included as order lines.
  const { unfiscalizedPss, canceledPssNotSignedWhenPurchased } =
    await getUnfiscalizedPss(booking, orderSessionId, lastSignedOnPOS);

  const groupedPss = groupedPSS(
    booking.pssList.filter(
      (pss) =>
        // do not consider abandoned pss
        pss.bookingStatus !== BookingStatus.Abandoned &&
        (pss.orderSessionId === orderSessionId ||
          canceledPSSIds.includes(pss.uuid) ||
          unfiscalizedPss.has(pss.uuid as string))
    ),
    { byOrderSessionId: true }
  );

  // This is used in case we have canceled PSS that were not signed when they were purchased,
  // in which case we need to add a sale line for them as well, so they can be signed on cancellation.
  // In this case we need to regroup order lines by price and time slot to avoid having multiple lines for the same ticket.
  let regroupOrderLine = false;
  for (const pssGroup of groupedPss) {
    const orderLineId = generateUuidv4();
    const totalPriceWithDiscount = pssGroupTotalPriceWithDiscount(
      pssGroup,
      booking.accountings
    );
    const lineType = getLineType(pssGroup);
    const orderLineTax = getOrderLineTax(
      pssGroup,
      totalPriceWithDiscount,
      lineType
    );
    const unitPrice = pssGroup.overridenPrice
      ? pssGroup.overridenPrice.originalPrice / 100
      : getPSSValue(pssGroup) / 100;

    const overridePrice = pssGroup.overridenPrice
      ? pssGroup.overridenPrice.newPrice / 100
      : undefined;

    const totalPrice =
      (overridePrice || unitPrice) *
      pssGroup.quantity *
      (lineType === LineType.CancelLine ? -1 : 1);

    const orderLine: OrderLine = {
      itemType: pssGroup.pcType
        ? (pssGroup.pcType as unknown as OrderLineItemType) // order line item type is the same as PC type
        : OrderLineItemType.NORMAL,
      categoryId: String(pssGroup.categoryId),
      priceId: String(pssGroup.priceId),
      timeSlotId: pssGroup.timeSlotId || undefined,
      retailOptionId: (pssGroup.retailOptionId as any) || undefined,
      id: orderLineId,
      quantity: pssGroup.quantity * (lineType === LineType.CancelLine ? -1 : 1),
      lineType: lineType,
      mainLineId:
        lineType === LineType.SaleLine
          ? getAddonMainLineId(pssGroup, orderLines, booking.pssList)
          : await getCancellationMainLineId(
              booking,
              pssGroup,
              orderSessionId,
              orderLines,
              lastSignedOnPOS
            ), // Main order line ID for linked addons / OR cancellation main line id
      mainLineOrderSessionId:
        lineType === LineType.CancelLine || pssGroup.mainTicketUUID
          ? pssGroup.orderSessionId
          : undefined,
      addonType: getOrderLineLineAddonType(pssGroup),
      vatId: orderLineTax.taxId || 0, // VAT ID
      vatRate: orderLineTax.salesTaxRate / 10000,
      vatCode: orderLineTax.taxCode,
      vatValue: orderLineTax.salesTaxMicroValue / 1000000, // VAT in micro (e10-6) division
      vatType: orderLineTax.salesTaxIncluded
        ? VATType.INCLUDED
        : VATType.EXCLUDED,
      createdAt: new Date(),
      updatedAt: new Date(),
      orderSessionId: orderSessionId, // Associated order session ID
      priceName: pssGroup.priceName,
      categoryName: pssGroup.categoryName,
      unitPrice,
      overridePrice,
      totalPrice,
      totalPriceWithDiscount:
        (totalPriceWithDiscount / 100) *
        (lineType === LineType.CancelLine ? -1 : 1),
    };
    orderLines.push(orderLine);
    if (lineType === LineType.SaleLine) {
      continue;
    } // no further processing needed.

    const canceledPssIdsInGroup = pssGroup.pssIds.filter((pssId) =>
      canceledPssNotSignedWhenPurchased.has(pssId)
    );
    if (canceledPssIdsInGroup.length > 0) {
      // if some of the canceled PSS were not signed when purchased, we need to add a sale line for them as well so they can be signed on cancellation
      // reverse their cancellation order line to create a sale line
      const saleOrderLine = cloneDeep(orderLine);
      saleOrderLine.id = generateUuidv4();
      saleOrderLine.quantity = canceledPssIdsInGroup.length;
      saleOrderLine.lineType = LineType.SaleLine;
      saleOrderLine.totalPrice *= -1;
      saleOrderLine.totalPriceWithDiscount *= -1;
      saleOrderLine.vatValue *= -1;
      orderLines.push(saleOrderLine);
      // set regroupOrderLine to true in case there are similar lines that need to be regrouped after adding the sale line for canceled PSS that were not signed when purchased
      regroupOrderLine = true;
    }
  }

  if (regroupOrderLine) {
    // Merge sale lines that share the same product key (added when canceled PSS weren't signed at purchase time)
    const saleLineKey = (ol: OrderLine) =>
      ol.itemType === OrderLineItemType.RETAIL
        ? `${ol.priceId}|${ol.vatRate}|${ol.vatType}|${ol.addonType}|${ol.itemType}|${ol.retailOptionId}`
        : `${ol.priceId}|${ol.vatRate}|${ol.vatType}|${ol.addonType}|${ol.itemType}|${ol.timeSlotId}`;

    const merged = new Map<string, OrderLine>();
    const result: OrderLine[] = [];

    for (const ol of orderLines) {
      if (ol.lineType !== LineType.SaleLine) {
        result.push(ol);
        continue;
      }
      const key = saleLineKey(ol);
      const existing = merged.get(key);
      if (existing) {
        existing.quantity += ol.quantity;
        existing.totalPrice += ol.totalPrice;
        existing.totalPriceWithDiscount += ol.totalPriceWithDiscount;
        existing.vatValue += ol.vatValue;
      } else {
        merged.set(key, ol);
        result.push(ol);
      }
    }

    orderLines.length = 0;
    orderLines.push(...result);
  }

  return orderLines;
}

function getLineType(pssGroup: IQuantifiedProductSlotState): LineType {
  if (pssGroup.bookingStatus === BookingStatus.Canceled) {
    return LineType.CancelLine;
  }
  return LineType.SaleLine;
}

function getOrderLineLineAddonType(pssGroup: IQuantifiedProductSlotState) {
  let orderLineLineAddonType: LineAddonType | undefined;
  if (pssGroup.mainTicketUUID) {
    orderLineLineAddonType = pssGroup.isAutomaticAddon
      ? LineAddonType.AUTO_INCLUDED
      : LineAddonType.REGULAR;
  }
  return orderLineLineAddonType;
}

function getOrderLineTax(
  pssGroup: IQuantifiedProductSlotState,
  totalPriceWithDiscount: number,
  lineType: LineType
) {
  const tax: {
    salesTaxMicroValue: number;
    salesTaxRate: number;
    salesTaxIncluded: number;
    taxCode: string;
    taxId?: number;
  } = {
    salesTaxMicroValue: 0,
    salesTaxRate: 0,
    salesTaxIncluded: 0,
    taxCode: "",
  };
  const priceTaxes = getpssExtension(String(pssGroup.uuid))?.taxes || [];
  if (!priceTaxes.length) {
    return tax;
  }
  tax.salesTaxMicroValue = getTaxValue(totalPriceWithDiscount, priceTaxes[0]);
  if (lineType === LineType.CancelLine) {
    // cancellations become negative
    tax.salesTaxMicroValue = tax.salesTaxMicroValue * -1;
  }
  tax.salesTaxRate = Number(priceTaxes[0].rateMiliValue);
  tax.salesTaxIncluded = Number(priceTaxes[0].includedInPrice);
  tax.taxCode = String(priceTaxes[0].name);
  tax.taxId = Number(priceTaxes[0].id);
  return tax;
}

function getAddonMainLineId(
  pssGroup: IQuantifiedProductSlotState,
  orderLines: OrderLine[],
  pssList: IProductSlotState[]
) {
  if (!pssGroup.mainTicketUUID) return;
  const mainTicket = pssList.find(
    (pss) => pss.uuid === pssGroup.mainTicketUUID
  );
  if (!mainTicket) return;
  const mainOrderLine = orderLines.find(
    (orderLine) =>
      orderLine.priceId === String(mainTicket.priceId) &&
      orderLine.timeSlotId === mainTicket.timeSlotId
  );
  if (!mainOrderLine) return;
  return mainOrderLine.id;
}

/**
 * Translates booking discounts to order discount objects
 * @param booking
 */
async function getOrderDiscountsAndVouchersFromBooking(
  booking: IProductSlotBooking,
  orderSessionId: string,
  orderLines: OrderLine[],
  lastSignedOnPOS: dayjs.Dayjs
): Promise<{
  discounts: Discount[];
  discountConfigs: DiscountConfig[];
  vouchers: Voucher[];
}> {
  const orderDiscountConfigs: DiscountConfig[] = [];
  const orderDiscounts: Discount[] = [];
  const orderVouchers: Voucher[] = [];
  // Methods with transaction type payment, that are considered vouchers
  const voucherPaymentMethods = [
    PaymentMethods.POS_VOUCHER,
    PaymentMethods.POS_GIFT_CARD,
    PaymentMethods.EXTERNAL_INCERT_VOUCHER,
  ];
  // States in which accountings reduce order balance
  const validAccountingStates = [
    AccountingState.Completed,
    AccountingState.Pending,
    AccountingState.Started,
  ];

  // Track discounts/vouchers whose mainId could not be resolved during the loop
  // because the original was in the current (not yet remote) order session
  const pendingDiscountResolutions: {
    discount: Discount;
    accounting: IAccounting;
  }[] = [];
  const pendingVoucherResolutions: {
    voucher: Voucher;
    accounting: IAccounting;
  }[] = [];

  for (const accounting of booking.accountings) {
    if (
      accounting.orderSessionId !== orderSessionId &&
      dayjs(accounting.createdAt).isSameOrBefore(lastSignedOnPOS)
    ) {
      // ignore accountings that are not part of the target order session and were created before the last time the booking was signed on the POS
      continue;
    }
    if (
      accounting.transactionType !==
        TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
      !voucherPaymentMethods.includes(accounting.paymentMethod)
    ) {
      // ignore non discount/voucher accountings
      continue;
    }
    if (!validAccountingStates.includes(accounting.state)) {
      // ignore accountings with invalid states (canceled, failed ... etc)
      continue;
    }
    // One accounting can discount several tickets at once, and those tickets
    // can sit on different order lines. Walk the entries rather than the
    // accounting, so each line carries the discount that actually applies to
    // it instead of the whole amount landing on the first one.
    const accountingPssList = booking.pssList.filter((p) =>
      accounting.accountingEntriesList.some(
        (ae) => ae.productSlotStateUuid === p.uuid
      )
    );
    for (const accountingEntry of accounting.accountingEntriesList) {
      const pss = booking.pssList.find(
        (p) => p.uuid === accountingEntry.productSlotStateUuid
      );
      if (!pss) {
        LogService.info("No pss linked to discount accounting entry", {
          accountingUuid: accounting.uuid,
          accountingEntry,
        });
        continue;
      }
      let lineId = await getDiscountedOrderLine(
        orderSessionId,
        [pss],
        orderLines,
        booking,
        lastSignedOnPOS
      );
      if (!lineId) {
        // The entry's own ticket dead-ended. Fall back to the rest of the
        // accounting's tickets rather than dropping the entry: a partial drop
        // leaves the order session short of the amount that was booked, which
        // the EFR rejects with "Somme fausse". Attributing the entry to a
        // sibling line is the same compromise the whole accounting used to
        // make, now confined to the entries that cannot be placed exactly.
        const siblingPssList = accountingPssList.filter(
          (p) => p.uuid !== pss.uuid
        );
        if (siblingPssList.length) {
          lineId = await getDiscountedOrderLine(
            orderSessionId,
            siblingPssList,
            orderLines,
            booking,
            lastSignedOnPOS
          );
        }
      }
      if (!lineId) {
        // dropping a discount or voucher leaves the order session unbalanced, which the EFR rejects
        // with "Somme fausse", so this must be visible rather than a silent skip
        LogService.info(
          `No order line found for an entry of discount/voucher accounting ${accounting.uuid}, dropping it. The order session will not balance.`,
          {
            orderSessionId,
            accountingUuid: accounting.uuid,
            promocode: accounting.promocode,
            promoCodeId: accounting.promoCodeId,
            paymentMethod: accounting.paymentMethod,
            value: accountingEntry.value / 100,
            accountingValue:
              sumArray(accounting.accountingEntriesList.map((ae) => ae.value)) /
              100,
            pssUuid: pss.uuid,
            pssOrderSessionId: pss.orderSessionId,
            lastSignedOnPOS: lastSignedOnPOS.toISOString(),
          },
          LogLevel.Error
        );
        continue;
      }
      const discountValue = accountingEntry.value / 100;
      if (voucherPaymentMethods.includes(accounting.paymentMethod)) {
        // Voucher has no line of its own, so entries only collapse by code
        const existingVoucher = orderVouchers.find(
          (v) => v.code === accounting.promocode
        );
        if (existingVoucher) {
          existingVoucher.value += discountValue;
          continue;
        }
        const orderVoucher: Voucher = {
          id: generateUuidv4(),
          voucherId: String(accounting.promoCodeId), // Optional promocode ID
          code: accounting.promocode, // Discount code, nullable
          value: discountValue, // Voucher value, +/- based on cancel or applied
          mainVoucherId: accounting.refund
            ? await getDiscountCancellationMainLineId(
                booking,
                accounting,
                pss,
                orderSessionId,
                lastSignedOnPOS
              )
            : undefined, // Main discount ID for cancellation
          mainVoucherOrderSessionId: accounting.refund
            ? pss.orderSessionId
            : undefined,
          createdAt: new Date(),
          currency: accounting.currency as Currency,
        };
        orderVouchers.push(orderVoucher);
        if (accounting.refund && !orderVoucher.mainVoucherId) {
          pendingVoucherResolutions.push({ voucher: orderVoucher, accounting });
        }
      } else {
        const existingDiscount = orderDiscounts.find(
          (d) => d.lineId === lineId && d.code === accounting.promocode
        );
        if (existingDiscount) {
          existingDiscount.value += discountValue;
          continue;
        }
        // One config per code, reused — otherwise splitting a discount across
        // lines would multiply the configs describing the same code
        let discountConfig = orderDiscountConfigs.find(
          (dc) => dc.code === accounting.promocode
        );
        if (!discountConfig) {
          discountConfig = {
            id: generateUuidv4(),
            codeId: accounting.promoCodeId || undefined,
            code: accounting.promocode,
            // TODO:
            // flatDiscount?: number;
            // percentDiscount?: number;
          };
          orderDiscountConfigs.push(discountConfig);
        }
        const orderDiscount: Discount = {
          id: generateUuidv4(),
          codeId: accounting.promoCodeId || undefined, // Optional promocode ID
          code: accounting.promocode, // Discount code, nullable
          value: discountValue, // Discount value, +/- based on cancel or applied
          lineId: lineId, // ID of the discounted line
          mainDiscountId: accounting.refund
            ? await getDiscountCancellationMainLineId(
                booking,
                accounting,
                pss,
                orderSessionId,
                lastSignedOnPOS
              )
            : undefined, // Main discount ID for cancellation
          discountConfigId: discountConfig.id,
          discountConfigOrderSessionId: orderSessionId,
          createdAt: new Date(),
        };
        orderDiscounts.push(orderDiscount);
        if (accounting.refund && !orderDiscount.mainDiscountId) {
          pendingDiscountResolutions.push({
            discount: orderDiscount,
            accounting,
          });
        }
      }
    }
  }

  // Resolve pending mainDiscountId for items whose original was in the current (not yet remote) order session.
  // The target is read off the finished discount rather than recomputed from
  // its accounting: identical while one accounting produced one discount, and
  // still correct now that an accounting can be split across several lines.
  for (const { discount, accounting } of pendingDiscountResolutions) {
    const targetValue = discount.value * -1;
    const mainDiscount = orderDiscounts.find(
      (d) =>
        d.id !== discount.id &&
        d.value === targetValue &&
        d.codeId === (accounting.promoCodeId || undefined)
    );
    const mainVoucher = orderVouchers.find(
      (v) =>
        v.value === targetValue &&
        v.voucherId === String(accounting.promoCodeId)
    );
    discount.mainDiscountId =
      mainDiscount?.id || mainVoucher?.id || generateUuidv4();
  }

  // Resolve pending mainVoucherId for items whose original was in the current (not yet remote) order session
  for (const { voucher, accounting } of pendingVoucherResolutions) {
    const targetValue = voucher.value * -1;
    const mainDiscount = orderDiscounts.find(
      (d) =>
        d.value === targetValue &&
        d.codeId === (accounting.promoCodeId || undefined)
    );
    const mainVoucher = orderVouchers.find(
      (v) =>
        v.id !== voucher.id &&
        v.value === targetValue &&
        v.voucherId === String(accounting.promoCodeId)
    );
    voucher.mainVoucherId =
      mainDiscount?.id || mainVoucher?.id || generateUuidv4();
  }

  return {
    discounts: orderDiscounts,
    discountConfigs: orderDiscountConfigs,
    vouchers: orderVouchers,
  };
}

async function getDiscountedOrderLine(
  currentOrderSessionId: string,
  pssList: IProductSlotState[],
  orderLines: OrderLine[],
  booking: IProductSlotBooking,
  lastSignedOnPOS: dayjs.Dayjs
): Promise<string | undefined> {
  for (const pss of pssList) {
    const { orderSessionId: pssOrderSession } = pss;

    if (!pssOrderSession) {
      continue; // skip if pss has no order session ID
    }

    // check in the current order session lines
    if (pssOrderSession === currentOrderSessionId) {
      const orderLine = findMatchingOrderLine(orderLines, pss);
      if (orderLine) {
        return orderLine.id;
      }
    } else {
      // fetch and process order session data for other order sessions
      try {
        let orderSession = null;
        orderSession = await getOrderSessionById(pssOrderSession);
        if (!orderSession?.items?.length) {
          // if no order session or no items, then it was likely a ticket coming from a channel other than POS
          // in which case we have to find the first order session in which it was modified on the POS, where it should have an order line
          LogService.info(
            `No items found in order session with ID ${pssOrderSession}, trying to find first POS order session for PSS.`
          );
          const fallBackOrderSessionId =
            getFirstPOSOrderSessionIdForPSS(booking, pss, lastSignedOnPOS) ||
            currentOrderSessionId;
          if (fallBackOrderSessionId === currentOrderSessionId) {
            // we do not use getOrderSessionById if it's the first time this ticket is being signed on the POS
            // as it hasn't been signed yet
            const orderLine = findMatchingOrderLine(orderLines, pss);
            if (orderLine) {
              return orderLine.id;
            }
          }
          orderSession = fallBackOrderSessionId
            ? await getOrderSessionById(fallBackOrderSessionId)
            : null;
          if (!orderSession?.items?.length) {
            LogService.info(
              `No items found in fallback POS order session ${fallBackOrderSessionId} for PSS with UUID ${pss.uuid}.`
            );
            continue;
          }
        }

        const orderLine = findMatchingOrderLine(
          orderSession.items[0].orderLines as OrderLine[],
          pss
        );
        if (orderLine) {
          return orderLine.lineId;
        }
      } catch (error) {
        LogService.info(
          `Failed to fetch order session with ID ${pssOrderSession}:`,
          error
        );
        continue; // Log the error and continue to the next product slot state
      }
    }
  }

  /**
   * No signed order session holds the line. If the ticket was absorbed into the order session
   * being built by cross-session fiscalization, its line is here and the discount has to point at
   * it, otherwise the caller drops the discount and the EFR receives positions that nothing
   * balances.
   */
  for (const pss of pssList) {
    const orderLine = findMatchingOrderLine(orderLines, pss);
    if (orderLine) {
      LogService.info(
        `Discounted order line for PSS with UUID ${pss.uuid} resolved to line ${orderLine.id} in the order session being built.`
      );
      return orderLine.id;
    }
  }

  return undefined; // Return undefined if no matching order line is found
}

function findMatchingOrderLine(
  orderLines: OrderLine[],
  pss: IProductSlotState
): (OrderLine & { lineId?: string }) | undefined {
  const { priceId, pssType, timeSlotId, retailOptionId } = pss;

  return orderLines.find((orderLine) => {
    const isMatchingType =
      pssType === PSSType[PSSType.Ticket]
        ? orderLine.timeSlotId === timeSlotId
        : orderLine.retailOptionId === retailOptionId;

    return orderLine.priceId === String(priceId) && isMatchingType;
  });
}

async function getOrderPaymentsFromBooking(
  booking: IProductSlotBooking,
  orderSessionId: string,
  lastSignedOnPOS: dayjs.Dayjs
): Promise<Payment[]> {
  const orderPayments: Payment[] = [];

  for (const accounting of booking.accountings) {
    if (
      accounting.orderSessionId !== orderSessionId &&
      dayjs(accounting.createdAt).isSameOrBefore(lastSignedOnPOS)
    ) {
      // ignore accountings that are not part of the target order session
      continue;
    }
    if (
      (accounting.transactionType !==
        TransactionTypes.TRANSACTION_TYPE_PAYMENT &&
        accounting.transactionType !==
          TransactionTypes.TRANSACTION_TYPE_REFUND) ||
      accounting.paymentMethod === PaymentMethods.POS_GIFT_CARD || // gift cards have a payment transaction type, but in order sessions they're considered a voucher
      accounting.paymentMethod === PaymentMethods.METHODS_POS_FREE // free payment method is used for free sales and doesn't correspond to a real payment
    ) {
      // ignore non-payment and non-refund accountings
      continue;
    }
    const orderPayment: Payment = {
      id: accounting.uuid as string,
      type: orderTransactionTypeFromAccounting(accounting),
      paymentMethod: posPaymentMethodToOrderPaymentMethod(
        accounting.paymentMethod
      ),
      transactionId: accounting.transactionId, // Transaction ID (e.g., Adyen, Wallee)
      processor:
        accounting.paymentMethod === PaymentMethods.METHOD_POS_ADYEN
          ? PaymentProcessor.ADYEN
          : PaymentProcessor.ORGANISER,
      value:
        sumArray(accounting.accountingEntriesList.map((ae) => ae.value)) / 100, // Payment value
      state: accountingStateToOrderPaymentState(accounting.state), // State of the payment accounting
      multiCurrencyInfo: getMultiCurrencyInfoFromAccounting(accounting),
      adyenPaymentInfo: getAdyenPaymentInfoFromAccounting(accounting),
      createdAt: dayjs(accounting.createdAt).toDate(),
      updatedAt: dayjs(accounting.updatedAt).toDate(),
    };

    if (accounting.paymentMethod === PaymentMethods.METHOD_POS_CASH) {
      // Belgium: the cash line reports what was actually handed over, so it
      // absorbs the rounding booked against the same session.
      const roundingAccountings = booking.accountings.filter(
        (acc) =>
          acc.orderSessionId === accounting.orderSessionId &&
          acc.paymentMethod === PaymentMethods.METHOD_CASH_ROUNDING &&
          acc.transactionType === accounting.transactionType
      );
      const totalRoundingAmount =
        sumArray(
          roundingAccountings
            .flatMap((acc) => acc.accountingEntriesList)
            .map((entry) => entry.value)
        ) / 100;
      orderPayment.value = roundToTwoDecimals(
        totalRoundingAmount + orderPayment.value
      );
    }

    if (accounting.paymentMethod === PaymentMethods.METHOD_CASH_ROUNDING) {
      // Negated as specified in the Belgian compliance documentation. Together
      // with the absorption above, the payments still sum to the sale total.
      orderPayment.value *= -1;
    }

    orderPayments.push(orderPayment);
  }

  return orderPayments;
}

export function bookingStateToOrderState(bookingState: BookingState) {
  switch (bookingState) {
    case BookingState.Completed:
      return OrderSessionState.COMPLETED;
    case BookingState.Abandoned:
    case BookingState.Canceled:
    case BookingState.Impossible:
      return OrderSessionState.ABANDONED;
    default:
      return OrderSessionState.PENDING;
  }
}

function pssGroupTotalPriceWithDiscount(
  pssGroup: IQuantifiedProductSlotState,
  accountings: IAccounting[]
) {
  const totalPriceWithoutDiscount = getPSSValue(pssGroup) * pssGroup.quantity;
  const promocodeAccountings = accountings.filter(
    (acc) =>
      acc.refund !== 1 &&
      acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
      (acc.state === AccountingState.Completed ||
        acc.state === AccountingState.Pending)
  );

  if (!promocodeAccountings || !promocodeAccountings.length) {
    // there are no promocode discount accountings
    return totalPriceWithoutDiscount;
  }

  const rowPssListUuids = pssGroup.pssIds;
  let totalDiscount = 0;
  for (const acc of promocodeAccountings) {
    const promocodeAEs = acc.accountingEntriesList.filter((ae) =>
      rowPssListUuids.some(
        (element) => element === ae.productSlotStateUuid && ae.value > 0
      )
    );
    if (!promocodeAEs || !promocodeAEs.length) continue;
    totalDiscount += sumArray(promocodeAEs.map((it) => it.value));
  }
  return totalPriceWithoutDiscount - totalDiscount;
}

function orderTransactionTypeFromAccounting(
  accounting: IAccounting
): TransactionType {
  //TODO: add cash correction gain/loss
  switch (accounting.transactionType) {
    case TransactionTypes.TRANSACTION_TYPE_PAYMENT:
      return TransactionType.PAYMENT;
    case TransactionTypes.TRANSACTION_TYPE_REFUND:
      return TransactionType.REFUND;
    case TransactionTypes.TRANSACTION_TYPE_CASH_TRANSFER:
      return TransactionType.CASH_TRANSFER;
    case TransactionTypes.TRANSACTION_TYPE_DISCOUNT:
      return TransactionType.DISCOUNT;
    case TransactionTypes.TRANSACTION_TYPE_FEE:
      return TransactionType.PAYMENT_FEE_USER;
    case TransactionTypes.TRANSACTION_TYPE_TAX:
      return TransactionType.TAX;
    case TransactionTypes.TRANSACTION_TYPE_TICKET:
      return TransactionType.TICKET;
  }
}

function accountingStateToOrderPaymentState(
  accountingState: AccountingState
): PaymentState {
  switch (accountingState) {
    case AccountingState.Authorized:
      return PaymentState.AUTHORIZED;
    case AccountingState.Cancelled:
      return PaymentState.CANCELLED;
    case AccountingState.Completed:
      return PaymentState.COMPLETED;
    case AccountingState.Failed:
      return PaymentState.FAILED;
    case AccountingState.Pending:
      return PaymentState.PENDING;
    case AccountingState.Started:
      return PaymentState.STARTED;
    case AccountingState.Tokenized:
      return PaymentState.TOKENIZED;
  }
}

function getMultiCurrencyInfoFromAccounting(
  accounting: IAccounting
): MultiCurrencyInfo | undefined {
  if (
    accounting.paymentMethod !== PaymentMethods.METHOD_POS_CASH ||
    accounting.paymentCurrency === accounting.currency
  ) {
    return;
  }
  const { paymentCurrency, providedCashBill, exchangeRate } = accounting;
  if (!paymentCurrency || !providedCashBill || !exchangeRate) {
    return;
  }
  return {
    paymentCurrency,
    providedCashBill,
    exchangeRate,
  };
}

function getAdyenPaymentInfoFromAccounting(
  accounting: IAccounting
): AdyenPaymentInfo | undefined {
  if (accounting.paymentMethod !== PaymentMethods.METHOD_POS_ADYEN) {
    return;
  }
  const {
    merchantReference,
    transactionTimestamp,
    saleId,
    poiId,
    splitReference,
    accountCode,
    pspReference,
    transactionId,
  } = accounting;
  if (
    !merchantReference ||
    !transactionTimestamp ||
    !saleId ||
    !poiId ||
    !splitReference ||
    !accountCode ||
    !pspReference ||
    !transactionId
  ) {
    return;
  }
  return {
    merchantReference,
    transactionTimestamp,
    saleId,
    poiId,
    splitReference,
    accountCode,
    pspReference,
    transactionId,
  };
}

function posPaymentMethodToOrderPaymentMethod(
  accountingPaymentMethod: PaymentMethods
): PaymentMethod {
  if (
    Object.values(PaymentMethod).includes(
      accountingPaymentMethod as unknown as PaymentMethod // we assume that PaymentMethods and PaymentMethod sufficiently overlap
    )
  ) {
    return accountingPaymentMethod as unknown as PaymentMethod;
  }
  throw new Error(
    `Value "${accountingPaymentMethod}" is not a valid member of PaymentMethod`
  );
}

function getOrderVatByType(orderLines: OrderLine[]) {
  let vatIncluded = 0;
  let vatExcluded = 0;
  for (const orderLine of orderLines) {
    if (orderLine.vatType === VATType.INCLUDED) {
      vatIncluded += orderLine.vatValue;
    } else {
      vatExcluded += orderLine.vatValue;
    }
  }
  return { vatIncluded, vatExcluded };
}

export function bookingCurrencyToOrderCurrency(bookingCurrency: Currencies) {
  switch (bookingCurrency) {
    case Currencies.AED:
      return Currency.AED;
    case Currencies.CHF:
      return Currency.CHF;
    case Currencies.EUR:
      return Currency.EUR;
    case Currencies.GBP:
      return Currency.GBP;
    case Currencies.USD:
      return Currency.USD;
  }
}

export function getSessionOrderType(
  booking: IProductSlotBooking,
  orderSession: OrderSession
): OrderType {
  // if all tickets/products of a booking were canceled in this session, it is considered a full cancelation
  // if some but not all tickets/product were canceled, then it is a partial cancelation aka correction
  // otherwise, in case of no cancelations, it is considered a new sale
  let orderType = OrderType.NEW_SALE;
  const pssList = booking.pssList;

  // if an order contains at least one sale line, or the count of its cancelation lines does not match the pss count in the booking
  // then it cannot be a full cancellation
  // canBeFullCancelation will be used to minimize the checks

  // 1- Check if the order contains any sale lines
  const hasSaleLines = orderSession.orderLines.some(
    (orderLine) => orderLine.lineType === LineType.SaleLine
  );

  // Sum the quantities of cancellation lines in the order session
  const cancellationQuantitySum =
    orderSession.orderLines
      .filter((orderLine) => orderLine.lineType === LineType.CancelLine)
      .reduce((sum, orderLine) => sum + orderLine.quantity, 0) * -1; // has to be converted back to a positive value

  // 3- Determine if the order can be a full cancellation
  const canBeFullCancelation =
    !hasSaleLines && cancellationQuantitySum === booking.pssList.length;

  // this variable will be used to check if the number of pss canceled in the given order matches the total count of pss in the booking
  // which will determine if it's a full or partial cancelation
  let pssCanceledInOrderCount = 0;

  for (const pss of pssList) {
    if (pss.bookingStatus !== BookingStatus.Canceled) {
      continue;
    }

    // check if this ticket was canceled in the given order session and change type accordingly
    const cancellationLine = orderSession.orderLines.find((orderLine) => {
      if (pss.pssType === PSSType[PSSType.Ticket]) {
        // if its a ticket, compare price id and timeslots
        return (
          orderLine.lineType === LineType.CancelLine &&
          orderLine.priceId === String(pss.priceId) &&
          orderLine.timeSlotId === pss.timeSlotId
        );
      } else {
        // otherwise compare price id and retail option ids
        return (
          orderLine.lineType === LineType.CancelLine &&
          orderLine.priceId === String(pss.priceId) &&
          orderLine.retailOptionId === pss.retailOptionId
        );
      }
    });

    if (cancellationLine) {
      if (canBeFullCancelation) {
        // pss was canceled in this order session, and the order itself can be a full cancelation, we increase the count and continue checking
        pssCanceledInOrderCount++;
      } else {
        // pss was canceled in this order, but the order cannot be a full cancelation, therefore it is confirmed to be a partial correction
        orderType = OrderType.PARTIAL_CANCELLATION;
        break;
      }
    }
  }

  if (canBeFullCancelation && pssCanceledInOrderCount === pssList.length) {
    // all booking pss were canceled in this order, therefore, it is a full cancellation
    orderType = OrderType.FULL_CANCELLATION;
  }

  return orderType;
}

async function getCancellationMainLineId(
  booking: IProductSlotBooking,
  pssGroup: IQuantifiedProductSlotState,
  currentOrderSessionId: string,
  currentOrderLines: OrderLine[],
  lastSignedOnPOS: dayjs.Dayjs
) {
  // in case a main line id is not found for whatever reason, a new random uuid will be returned.
  // backend will recognize that it was not found and handle it accordingly
  const fallBackMainLineId = generateUuidv4();
  if (!pssGroup.orderSessionId) {
    // if a group of pss does not have an order session id, we use a new uuid for main line id
    return fallBackMainLineId;
  }
  const findMainLineInCurrentOrderLines = () => {
    const mainLine = currentOrderLines.find(
      (orderLine) =>
        orderLine.lineType === LineType.SaleLine &&
        orderLine.priceId === String(pssGroup.priceId) &&
        ((pssGroup.pssType === PSSType[PSSType.Ticket] &&
          orderLine.timeSlotId === pssGroup.timeSlotId) ||
          (pssGroup.pssType === PSSType[PSSType.Retail] &&
            orderLine.retailOptionId === pssGroup.retailOptionId))
    );
    return mainLine?.id || fallBackMainLineId;
  };
  if (pssGroup.orderSessionId === currentOrderSessionId) {
    return findMainLineInCurrentOrderLines();
  }
  let orderSession = null;
  try {
    orderSession = await getOrderSessionById(pssGroup.orderSessionId);
    if (!orderSession?.items?.length) {
      // if no order session or no items, then it was likely a ticket coming from a channel other than POS
      // in which case we have to find the first order session in which it was modified on the POS, where it should have an order line
      LogService.info(
        `No items found in order session with ID ${pssGroup.orderSessionId}, trying to find first POS order session for PSS.`
      );
      const fallBackOrderSessionId =
        getFirstPOSOrderSessionIdForPSS(booking, pssGroup, lastSignedOnPOS) ||
        currentOrderSessionId;
      if (fallBackOrderSessionId === currentOrderSessionId) {
        return findMainLineInCurrentOrderLines();
      }
      orderSession = fallBackOrderSessionId
        ? await getOrderSessionById(fallBackOrderSessionId)
        : null;
      if (!orderSession?.items?.length) {
        LogService.info(
          `No items found in fallback POS order session ${fallBackOrderSessionId} for PSS with UUID ${pssGroup.uuid}.`
        );
        return fallBackMainLineId;
      }
    }
  } catch (error) {
    LogService.info(
      `Failed to fetch order session with ID ${pssGroup.orderSessionId}:`,
      error
    );
    return fallBackMainLineId;
  }

  const orderLines = orderSession.items[0].orderLines;
  const mainOrderLine = orderLines.find(
    (orderLine) =>
      orderLine.lineType === "SaleLine" &&
      orderLine.priceId === String(pssGroup.priceId) &&
      // either pss group is of type ticket and timeslot id matches
      ((pssGroup.pssType === PSSType[PSSType.Ticket] &&
        orderLine.timeSlotId === pssGroup.timeSlotId) ||
        // or pss group is of type retail and retail option id matches
        (pssGroup.pssType === PSSType[PSSType.Retail] &&
          orderLine.retailOptionId === pssGroup.retailOptionId))
  );
  if (!mainOrderLine?.lineId) {
    return fallBackMainLineId;
  }
  return mainOrderLine.lineId;
}

async function getDiscountCancellationMainLineId(
  booking: IProductSlotBooking,
  discountAccounting: IAccounting,
  pss: IProductSlotState | IQuantifiedProductSlotState, // to get the order session where the discount was applied
  currentOrderSessionId: string,
  lastSignedOnPOS: dayjs.Dayjs
) {
  // in case a main line id is not found for whatever reason, a new random uuid will be returned.
  // backend will recognize that it was not found and handle it accordingly
  const fallBackMainDiscountId = generateUuidv4();
  const originalOrderSession = pss.orderSessionId;
  if (!originalOrderSession) {
    return fallBackMainDiscountId;
  }
  if (originalOrderSession === currentOrderSessionId) {
    // current order session is not yet on remote — caller will resolve after the loop
    return undefined;
  }
  let orderSession = null;
  try {
    orderSession = await getOrderSessionById(originalOrderSession);
    if (!orderSession?.items?.length) {
      // if no order session or no items, then it was likely a ticket coming from a channel other than POS
      // in which case we have to find the first order session in which it was modified on the POS, where it should have an order line
      LogService.info(
        `No items found in order session with ID ${originalOrderSession}, trying to find first POS order session for PSS.`
      );
      const fallBackOrderSessionId =
        getFirstPOSOrderSessionIdForPSS(booking, pss, lastSignedOnPOS) ||
        currentOrderSessionId;
      if (fallBackOrderSessionId === currentOrderSessionId) {
        // current order session is not yet on remote — caller will resolve after the loop
        return undefined;
      }
      orderSession = fallBackOrderSessionId
        ? await getOrderSessionById(fallBackOrderSessionId)
        : null;
      if (!orderSession?.items?.length) {
        LogService.info(
          `No items found in fallback POS order session ${fallBackOrderSessionId} for PSS with UUID ${pss.uuid}.`
        );
        return fallBackMainDiscountId;
      }
    }
  } catch (error) {
    LogService.info(
      `Failed to fetch order session with ID ${originalOrderSession}:`,
      error
    );
    return fallBackMainDiscountId;
  }
  const discounts = orderSession.items[0].discounts;
  const mainDiscountLine = discounts?.find(
    (discountLine: any) =>
      discountLine.value ===
        (sumArray(
          discountAccounting.accountingEntriesList.map((ae) => ae.value)
        ) /
          100) *
          -1 &&
      discountLine.codeId === (discountAccounting.promoCodeId || undefined)
  );

  const vouchers = orderSession.items[0].vouchers;
  const mainVoucherLine = vouchers?.find(
    (voucherLine) =>
      voucherLine.value ===
        (sumArray(
          discountAccounting.accountingEntriesList.map((ae) => ae.value)
        ) /
          100) *
          -1 &&
      voucherLine.voucherId === (discountAccounting.promoCodeId || undefined)
  );
  if (!mainDiscountLine && !mainVoucherLine) {
    return fallBackMainDiscountId;
  }

  return mainDiscountLine?.id || mainVoucherLine?.id || fallBackMainDiscountId;
}

export function orderTypeToReceiptType(orderType: OrderType | undefined) {
  switch (orderType) {
    case OrderType.NEW_SALE:
      return t("receipt.type-new-sale");
    case OrderType.PARTIAL_CANCELLATION:
      return t("receipt.type-partial-cancellation");
    case OrderType.FULL_CANCELLATION:
      return t("receipt.type-full-cancellation");
    default:
      return;
  }
}

/**
 * Returns the country signature this device recorded on an order session when it signed it.
 * Local and therefore offline-safe, but only present for sessions signed on this device.
 */
function getSessionSignature(orderSession: BaseOrderSession) {
  if (isGermanCompliant.value) {
    return orderSession.signatureInfoDE;
  }
  if (isSpanishCompliant.value) {
    return orderSession.signatureInfoES;
  }
  if (isFrenchCompliant.value) {
    return orderSession.signatureInfoFR;
  }
  if (isCroatianCompliant.value) {
    return orderSession.signatureInfoHR;
  }
}

/**
 * Finds the last order session of a booking that was completed on the POS and signed.
 * The given order session is excluded from the search.
 *
 * This is the single source of truth for both the cross-session fiscalization cut-off
 * (see lastTimeSignedOnPOS) and the order session a REPRINT event has to reference
 * (see checkAndProcessReprint), so that the two can never disagree on what is already signed.
 *
 * A completed POS order session is not necessarily a signed one: its state and completionDate
 * are set before the sale event runs, so signing may still have failed. Only the certification
 * DB tells us, which is why undetermined is returned when it cannot be consulted.
 * @param booking
 * @param orderSessionId order session to exclude, normally the current one
 */
export async function findLastSignedPOSOrderSession(
  booking: IProductSlotBooking,
  orderSessionId: string
): Promise<{ session?: BaseOrderSession; undetermined: boolean }> {
  // find all order sessions that were made on the POS, excluding the current one
  const posOrderSessions = booking.orderSessions
    .filter(
      (os) =>
        os.channel?.toLowerCase() === "smeetz pos" &&
        os.id !== orderSessionId &&
        !!os.completionDate
    )
    .sort(
      (a, b) =>
        dayjs(b.completionDate).valueOf() - dayjs(a.completionDate).valueOf()
    );

  if (posOrderSessions.length === 0) {
    // nothing to look up, so nothing that could be left undetermined either
    LogService.info("No previous POS order sessions found for this booking.");
    return { undetermined: false };
  }

  const rejected: { id: string; completionDate?: string }[] = [];

  for (const os of posOrderSessions) {
    const signed = await isOrderSessionSigned(os.id);
    if (signed) {
      return { session: os, undetermined: false };
    }
    if (signed === undefined) {
      // certification DB unreachable, fall back to what this device recorded when it signed
      if (getSessionSignature(os)) {
        LogService.info(
          `Order session ${os.id} could not be checked against the certification DB, but this device holds its signature.`
        );
        return { session: os, undetermined: false };
      }
      // an older session cannot be preferred over this one: if this one turns out to be signed,
      // both the reprint target and the fiscalization cut-off would be wrong
      LogService.info(
        `Signed state of POS order session ${os.id} could not be determined, giving up the search.`
      );
      return { undetermined: true };
    }
    rejected.push({ id: os.id, completionDate: os.completionDate });
  }

  LogService.info(
    "No previous signed POS order session found for this booking.",
    { rejectedOrderSessions: rejected }
  );
  return { undetermined: false };
}

/**
 * This function returns the last time a booking was signed on the POS system.
 * Which is determined by finding the latest order session that was processed and fiscalized on the POS.
 * The current session is excluded from this check.
 * Throws when that cannot be established, because falling back to the default old date would
 * re-absorb and double-fiscalize tickets and payments that were already signed.
 * @param booking
 */
async function lastTimeSignedOnPOS(
  booking: IProductSlotBooking,
  orderSessionId: string
) {
  const { session, undetermined } = await findLastSignedPOSOrderSession(
    booking,
    orderSessionId
  );

  if (undetermined) {
    throw new Error(
      "Could not determine the last time this booking was signed on the POS"
    );
  }

  if (!session) {
    const defaultOldDate = dayjs("2001-12-17T00:00:00Z");
    // this cut-off makes every unfiscalized ticket, payment and discount in the booking get
    // absorbed into the order session being built and signed as a new sale, so record it plainly
    LogService.info(
      `No signed POS order session for this booking, using default old date ${defaultOldDate.toISOString()} as last signed time. Everything unfiscalized will be absorbed into order session ${orderSessionId} and signed.`
    );
    return defaultOldDate;
  }

  const lastSignDate = dayjs(session.completionDate);

  // return the last order session's completion date, which should be the last time signed on POS as all POS order sessions are fiscalized
  LogService.info(
    `Last time signed on POS found: ${lastSignDate.toISOString()} from order session ID ${
      session.id
    }.`
  );
  return lastSignDate;
}

/**
 * This function determines what was the first order session on the POS where a given PSS was included as an order line.
 * By finding the first POS accounting that includes the PSS.
 *
 * Only sessions that were completed at or before lastSignedOnPOS are nominated. A POS order
 * session completed after that cut-off is one we already established was not signed, so it holds
 * no order line for the caller to point at, and nominating it makes the caller dead-end instead of
 * falling back to the current order session where the line actually is.
 * @param booking
 * @param pss
 * @param lastSignedOnPOS cut-off returned by lastTimeSignedOnPOS
 */
function getFirstPOSOrderSessionIdForPSS(
  booking: IProductSlotBooking,
  pss: IProductSlotState | IQuantifiedProductSlotState,
  lastSignedOnPOS: dayjs.Dayjs
) {
  /**
   * An order session is only usable as a reference if it was signed, which the cut-off tells us.
   * Sessions missing from the booking cannot be checked, so they are accepted as before rather
   * than dropped.
   */
  const isSignedByCutOff = (orderSessionId: string | undefined) => {
    const orderSession = booking.orderSessions.find(
      (os) => os.id === orderSessionId
    );
    if (!orderSession) {
      return true;
    }
    return dayjs(orderSession.completionDate).isSameOrBefore(lastSignedOnPOS);
  };

  // find all accountings that include the given PSS
  const relatedAccountings = booking.accountings.filter((acc) =>
    acc.accountingEntriesList.some((ae) => ae.productSlotStateUuid === pss.uuid)
  );

  // filter to only those made on the POS
  const posAccountings = relatedAccountings.filter(
    (acc) => acc.channel?.toLowerCase() === "smeetz pos"
  );

  if (posAccountings.length === 0) {
    LogService.info(`No POS accountings found for PSS with UUID ${pss.uuid}.`);
    // Find the first accounting (any channel) that includes the PSS
    if (!relatedAccountings.length) {
      LogService.info(
        `No accountings at all found for PSS with UUID ${pss.uuid}, no POS order session nominated.`
      );
      return;
    }
    relatedAccountings.sort(
      (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf()
    );
    const firstAccounting = relatedAccountings[0];
    // Find the first POS order session created after that accounting
    const candidates = booking.orderSessions
      .filter(
        (os) =>
          os.channel?.toLowerCase() === "smeetz pos" &&
          os.state === OrderSessionState.COMPLETED &&
          dayjs(os.createdAt).isAfter(dayjs(firstAccounting.createdAt))
      )
      .sort(
        (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf()
      );
    const posOrderSession = candidates.find((os) => isSignedByCutOff(os.id));
    if (!posOrderSession) {
      LogService.info(
        `No signed POS order session nominated for PSS with UUID ${pss.uuid}.`,
        {
          lastSignedOnPOS: lastSignedOnPOS.toISOString(),
          firstAccountingCreatedAt: firstAccounting.createdAt,
          rejectedCandidates: candidates.map((os) => ({
            id: os.id,
            completionDate: os.completionDate,
          })),
        }
      );
      return;
    }
    LogService.info(
      `POS order session ${posOrderSession.id} nominated for PSS with UUID ${pss.uuid}.`,
      { lastSignedOnPOS: lastSignedOnPOS.toISOString() }
    );
    return posOrderSession.id;
  }

  // sort by creation date to find the earliest
  posAccountings.sort(
    (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf()
  );

  const firstPOSAccounting = posAccountings[0];
  if (!isSignedByCutOff(firstPOSAccounting.orderSessionId)) {
    LogService.info(
      `First POS accounting for PSS with UUID ${pss.uuid} points at order session ${firstPOSAccounting.orderSessionId}, which was not signed, so it is not nominated.`,
      { lastSignedOnPOS: lastSignedOnPOS.toISOString() }
    );
    return;
  }
  LogService.info(
    `First POS accounting for PSS with UUID ${pss.uuid} found in order session ID ${firstPOSAccounting.orderSessionId}.`
  );
  return firstPOSAccounting.orderSessionId;
}
